import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, RefreshCw, CheckCircle2, XCircle, ShoppingCart } from "lucide-react";

const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(Number(ts) * 1000).toLocaleString();
  } catch {
    return "—";
  }
};

const fmtMoney = (v, cur) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(2)} ${cur || "USD"}`;
};

const Purchases = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/purchases", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div>
      <PageHeader
        eyebrow="Records"
        title="Purchase History"
        description="Every domain registration attempt logged with balance, charged amount and Namecheap response."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="btn-buy-new"
              variant="outline"
              onClick={() => navigate("/buy")}
              className="rounded-sm"
            >
              <ShoppingCart className="w-4 h-4 mr-2" /> Buy domain
            </Button>
            <Button
              data-testid="btn-refresh-history"
              variant="outline"
              onClick={load}
              disabled={loading}
              className="rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        }
      />
      <Section>
        <div className="flex items-center gap-2 mb-4">
          {["all", "ok", "error"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border ${
                statusFilter === s
                  ? "brand-bg text-white border-transparent"
                  : "border-[var(--border)] text-[var(--text-secondary)]"
              }`}
            >
              {s === "all" ? `All (${total})` : s}
            </button>
          ))}
        </div>

        <Card className="rounded-sm shadow-none border-[var(--border)]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-[var(--text-secondary)]">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading purchases...
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-sm text-[var(--text-secondary)]">
                No purchases yet.
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => navigate("/buy")}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" /> Buy your first domain
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="purchases-table">
                  <thead className="bg-[#FAFAFA] text-[var(--text-secondary)]">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">When</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Domain</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Years</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Status</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Charged</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Balance after</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Env</th>
                      <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium">Order / Txn</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {items.map((it) => {
                      const ok = it.status === "ok";
                      const resp = it.namecheap_response || {};
                      const order = resp.OrderID || resp.orderID || "";
                      const txn = resp.TransactionID || resp.transactionID || "";
                      const ba = it.balance_after || {};
                      return (
                        <tr key={it.id} data-testid={`row-${it.id}`} className="dense-row">
                          <td className="px-4 py-3 mono text-xs whitespace-nowrap">{fmtDate(it.created_at)}</td>
                          <td className="px-4 py-3 mono">{it.domain}</td>
                          <td className="px-4 py-3 mono">{it.years}</td>
                          <td className="px-4 py-3">
                            {ok ? (
                              <span className="inline-flex items-center gap-1.5 text-[var(--success)] text-xs uppercase tracking-wider mono">
                                <CheckCircle2 className="w-3.5 h-3.5" /> OK
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-[var(--error)] text-xs uppercase tracking-wider mono">
                                <XCircle className="w-3.5 h-3.5" /> Error
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 mono">
                            {it.charged != null
                              ? fmtMoney(it.charged, it.currency || ba.Currency)
                              : resp.ChargedAmount
                                ? `${resp.ChargedAmount} ${ba.Currency || ""}`
                                : "—"}
                          </td>
                          <td className="px-4 py-3 mono text-xs">
                            {ba.AvailableBalance ? fmtMoney(ba.AvailableBalance, ba.Currency) : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={`rounded-sm text-[10px] uppercase tracking-wider ${
                                it.sandbox ? "text-yellow-700 border-yellow-300" : "text-green-700 border-green-300"
                              }`}
                            >
                              {it.sandbox ? "Sandbox" : "Production"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 mono text-xs">
                            {ok ? (
                              <>
                                {order && <div>O: {order}</div>}
                                {txn && <div>T: {txn}</div>}
                                {!order && !txn && "—"}
                              </>
                            ) : (
                              <span className="text-[var(--error)]" title={it.error}>
                                {(it.error || "").slice(0, 60) || "—"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </Section>
    </div>
  );
};

export default Purchases;
